import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import SelectBuscable from '@/shared/components/SelectBuscable'
import {
  Upload, FileText, PencilLine, Trash2, CheckCircle, XCircle,
  ArrowLeft, Eye, EyeOff, RefreshCw, CheckCircle2
} from 'lucide-react'
import { apiService } from '@/services/api'
import { Notification, useNotification } from '@/shared/components/Notification'
import ConfirmActionModal from '@/shared/components/ConfirmActionModal'
import {
  MEXICAN_STATES, US_STATES, US_CITIES, SAT_FISCAL_REGIMES,
  SAT_CFDI_USAGE, MEXICAN_BANKS, SERVICE_TYPES
} from '@/constants/supplierConstants'
import { config } from '@/config/env'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { idSaiParaAlta, validarIdSai } from '@/features/proveedores/lib/idSai'

interface CreateSupplierFormProps {
  onBack: () => void
  onSuccess?: () => void
}

type FormStep = 1 | 2 | 3 | 4 | 5

interface LocalDocument {
  documentTypeId: string
  documentTypeName: string
  file: File
  fileName: string
}

interface FormData {
  // Step 1 - Datos generales
  razonSocial: string
  nombreComercial: string
  saiSupplierId: string
  tipoServicio: string
  contactoPrincipal: string
  telefono: string
  correoVentas: string
  correoCuentas: string
  // Step 2 - Info fiscal
  rfc: string
  tipoProveedor: string
  regimenFiscal: string
  usoCFDI: string
  correoFiscal: string
  telefonoFiscal: string
  pais: string
  estado: string
  ciudad: string
  municipio: string
  calle: string
  numero: string
  colonia: string
  cp: string
  // Step 3 - Info bancaria
  clabe: string
  banco: string
  nombreCuenta: string
  numeroCuenta: string
  moneda: string
  customBankName: string
  // Step 5 - Datos de acceso
  contactFirstName: string
  contactLastName: string
  email: string
  password: string
  isActive: boolean
}

const CreateSupplierForm = ({ onBack, onSuccess }: CreateSupplierFormProps) => {
  const [currentStep, setCurrentStep] = useState<FormStep>(1)
  const [isSaving, setIsSaving] = useState(false)
  const [localDocuments, setLocalDocuments] = useState<LocalDocument[]>([])
  const [documentTypes, setDocumentTypes] = useState<{ id: string; code: string; name: string; description?: string | null; isRequired: boolean; allowedMimeTypes?: string[]; personType?: string | null; maxFileSizeMb?: number }[]>([])
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showExitModal, setShowExitModal] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [availableColonias, setAvailableColonias] = useState<string[]>([])
  const [savingProgress, setSavingProgress] = useState<string>('')
  const [bankFile, setBankFile] = useState<File | null>(null)
  const [createdSupplierId, setCreatedSupplierId] = useState<string | null>(null)
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set())

  const { notification, showSuccess, showError, hideNotification } = useNotification()

  const {
    register,
    control,
    watch,
    formState: { errors, isDirty },
    setValue,
    trigger
  } = useForm<FormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      razonSocial: '', nombreComercial: '', saiSupplierId: '', tipoServicio: '',
      contactoPrincipal: '', telefono: '', correoVentas: '', correoCuentas: '',
      rfc: '', tipoProveedor: '', regimenFiscal: '', usoCFDI: '',
      correoFiscal: '', telefonoFiscal: '',
      pais: '', estado: '', ciudad: '', municipio: '',
      calle: '', numero: '', colonia: '', cp: '',
      clabe: '', banco: '', nombreCuenta: '', numeroCuenta: '', moneda: 'MXN', customBankName: '',
      contactFirstName: '', contactLastName: '', email: '', password: '',
      isActive: true
    }
  })

  const formData = watch()

  // Track if user has entered any data
  const hasUnsavedData = isDirty || localDocuments.length > 0 || bankFile !== null

  // Warn on browser close/refresh
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedData) {
        e.preventDefault()
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedData])

  // Safe back navigation
  const handleBack = () => {
    if (hasUnsavedData) {
      setShowExitModal(true)
    } else {
      onBack()
    }
  }

  // Postal code auto-fill
  const watchedCp = watch('cp')
  const watchedPais = watch('pais')

  useEffect(() => {
    const fetchPostalCodeData = async () => {
      if (watchedPais === 'MX' && watchedCp && watchedCp.length === 5 && /^\d+$/.test(watchedCp)) {
        try {
          const response = await fetch(`${config.apiBaseUrl}/catalogos/postal-codes/${watchedCp}`)
          if (response.ok) {
            const data = await response.json()
            if (data.municipio) setValue('municipio', data.municipio, { shouldValidate: true })
            if (data.estado) {
              const stateObj = MEXICAN_STATES.find(s =>
                s.name.toLowerCase() === data.estado.toLowerCase() || s.name === data.estado
              )
              if (stateObj) setValue('estado', stateObj.code, { shouldValidate: true })
            }
            if (data.colonias && Array.isArray(data.colonias) && data.colonias.length > 0) {
              setAvailableColonias(data.colonias)
              if (data.colonias.length === 1) {
                setValue('colonia', data.colonias[0], { shouldValidate: true })
              } else {
                const currentColonia = watch('colonia')
                if (currentColonia && !data.colonias.includes(currentColonia)) {
                  setValue('colonia', '', { shouldValidate: true })
                }
              }
            } else {
              setAvailableColonias([])
            }
          }
        } catch {
          setAvailableColonias([])
        }
      } else {
        if (watchedPais !== 'MX' || !watchedCp || watchedCp.length < 5) {
          setAvailableColonias([])
        }
      }
    }
    fetchPostalCodeData()
  }, [watchedCp, watchedPais, setValue])

  // Load document types catalog
  // Detect person type from RFC
  const watchedRfc = watch('rfc')
  const detectedPersonType: 'corporate' | 'individual' | 'foreign' | null = (() => {
    if (!watchedRfc) return null
    const cleanRfc = watchedRfc.trim().toUpperCase()
    if (/^XEXX\d{6}[A-Z0-9]{3}$/.test(cleanRfc)) return 'foreign'           // 13 chars = Extranjero
    if (/^[A-ZÑ&]{3}\d{6}[A-Z0-9]{3}$/.test(cleanRfc)) return 'corporate'   // 12 chars = Persona Moral
    if (/^[A-ZÑ&]{4}\d{6}[A-Z0-9]{3}$/.test(cleanRfc)) return 'individual'   // 13 chars = Persona Física
    return null
  })()

  // Fetch supplier document types from backend (filtered by personType when available)
  useEffect(() => {
    const loadDocumentTypes = async () => {
      try {
        const url = detectedPersonType
          ? `/admin/suppliers/document-types?personType=${detectedPersonType}`
          : '/admin/suppliers/document-types/all'
        const response = await apiService.request<{ id: string; code: string; name: string; description?: string | null; isRequired: boolean; personType?: string | null; allowedMimeTypes?: string[]; maxFileSizeMb?: number }[]>(url)
        setDocumentTypes(Array.isArray(response) ? response : [])
      } catch {
        setDocumentTypes([])
      }
    }
    loadDocumentTypes()
  }, [detectedPersonType])

  // Clean up local documents that no longer apply when person type changes
  useEffect(() => {
    if (documentTypes.length === 0) return
    const validDocTypeIds = new Set(documentTypes.map(dt => dt.id))
    setLocalDocuments(prev => {
      const filtered = prev.filter(d => validDocTypeIds.has(d.documentTypeId))
      return filtered.length !== prev.length ? filtered : prev
    })
  }, [documentTypes])

  // Password validation
  const validatePassword = (pwd: string) => ({
    minLength: pwd.length >= 8,
    hasUpperCase: /[A-Z]/.test(pwd),
    hasLowerCase: /[a-z]/.test(pwd),
    hasNumber: /[0-9]/.test(pwd),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(pwd)
  })

  const password = watch('password')
  const passwordValidation = validatePassword(password || '')
  const isPasswordValid = Object.values(passwordValidation).every(v => v)

  const generatePassword = () => {
    const length = 12
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*'
    let newPassword = ''
    newPassword += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)]
    newPassword += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)]
    newPassword += '0123456789'[Math.floor(Math.random() * 10)]
    newPassword += '!@#$%^&*'[Math.floor(Math.random() * 8)]
    for (let i = newPassword.length; i < length; i++) {
      newPassword += charset[Math.floor(Math.random() * charset.length)]
    }
    newPassword = newPassword.split('').sort(() => Math.random() - 0.5).join('')
    setValue('password', newPassword, { shouldValidate: true, shouldDirty: true })
  }

  // Local document handling
  const handleLocalFileAdd = (documentTypeId: string, documentTypeName: string, file: File) => {
    setLocalDocuments(prev => {
      const filtered = prev.filter(d => d.documentTypeId !== documentTypeId)
      return [...filtered, { documentTypeId, documentTypeName, file, fileName: file.name }]
    })
  }

  const handleLocalFileRemove = (documentTypeId: string) => {
    setLocalDocuments(prev => prev.filter(d => d.documentTypeId !== documentTypeId))
  }

  const handleFileInputChange = (documentTypeId: string, documentTypeName: string) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) handleLocalFileAdd(documentTypeId, documentTypeName, file)
    }

  // Step validation
  const validateStep = async (step: number): Promise<boolean> => {
    let fieldsToValidate: (keyof FormData)[] = []
    switch (step) {
      case 1:
        fieldsToValidate = ['razonSocial', 'saiSupplierId', 'tipoServicio', 'contactoPrincipal', 'telefono', 'correoVentas']
        break
      case 2: {
        const isOtro = watch('pais') === 'OTRO'
        fieldsToValidate = ['rfc', 'regimenFiscal', 'usoCFDI', 'correoFiscal', 'telefonoFiscal', 'pais', 'cp', 'estado', 'calle']
        if (!isOtro) {
          fieldsToValidate.push('municipio', 'colonia', 'numero')
        }
        break
      }
      case 3:
        fieldsToValidate = ['clabe', 'banco', 'nombreCuenta']
        if (watch('banco') === 'Otro') {
          fieldsToValidate.push('customBankName')
        }
        // Validar que la carátula bancaria esté presente
        if (!bankFile) {
          showError('Carátula requerida', 'Debes adjuntar la carátula bancaria para continuar.')
          return false
        }
        break
      case 4:
        return true // Documents are optional at creation
      case 5:
        fieldsToValidate = ['contactFirstName', 'contactLastName', 'email', 'password']
        break
    }
    if (fieldsToValidate.length > 0) {
      return await trigger(fieldsToValidate)
    }
    return true
  }

  // Save everything at the end
  const handleFinalSubmit = async () => {
    setIsSaving(true)
    setShowConfirmModal(false)

    const markDone = (step: string) => setCompletedSteps(prev => new Set(prev).add(step))

    try {
      // 1. Create supplier account (skip if already created)
      let supplierId = createdSupplierId
      if (!supplierId) {
        setSavingProgress('Creando cuenta del proveedor...')
        const createResponse = await apiService.request<{ supplier: { id: string } }>('/admin/suppliers/simple', {
          method: 'POST',
          body: JSON.stringify({
            contactFirstName: formData.contactFirstName,
            contactLastName: formData.contactLastName,
            email: formData.email,
            password: formData.password,
            isActive: formData.isActive,
            saiSupplierId: idSaiParaAlta(formData.saiSupplierId)
          })
        })
        supplierId = createResponse.supplier.id
        setCreatedSupplierId(supplierId)
        markDone('account')
      }

      // 2. Save general data (skip if already done)
      if (!completedSteps.has('general')) {
        setSavingProgress('Guardando datos generales...')
        await apiService.request(`/admin/suppliers/${supplierId}`, {
          method: 'PUT',
          body: JSON.stringify({
            legalName: formData.razonSocial,
            commercialName: formData.nombreComercial,
            serviceTypeId: formData.tipoServicio,
            mainContactName: formData.contactoPrincipal,
            mainContactPhone: formData.telefono,
            salesEmail: formData.correoVentas,
            accountsPayableEmail: formData.correoCuentas
          })
        })
        markDone('general')
      }

      // 3. Save fiscal data (skip if already done)
      if (!completedSteps.has('fiscal')) {
        setSavingProgress('Guardando información fiscal...')
        await apiService.request(`/admin/suppliers/${supplierId}/fiscal`, {
          method: 'PUT',
          body: JSON.stringify({
            taxRegime: formData.regimenFiscal,
            cfdiUse: formData.usoCFDI,
            taxEmail: formData.correoFiscal,
            taxPhone: formData.telefonoFiscal,
            rfc: formData.rfc
          })
        })
        markDone('fiscal')
      }

      // 4. Save address (skip if already done)
      if (!completedSteps.has('address')) {
        const isOtro = formData.pais === 'OTRO'
        await apiService.request(`/admin/suppliers/${supplierId}`, {
          method: 'PUT',
          body: JSON.stringify({
            country: formData.pais,
            state: formData.estado,
            city: isOtro ? '' : formData.municipio,
            municipality: isOtro ? '' : formData.municipio,
            postalCode: formData.cp,
            street: formData.calle,
            externalNumber: isOtro ? '' : formData.numero,
            suburb: isOtro ? '' : formData.colonia
          })
        })
        markDone('address')
      }

      // 5. Save bank data (skip if already done)
      if (!completedSteps.has('bank')) {
        setSavingProgress('Guardando información bancaria...')
        const bankName = formData.banco === 'Otro' ? formData.customBankName : formData.banco
        const token = localStorage.getItem('access_token')

        if (bankFile) {
          const bankFormData = new FormData()
          bankFormData.append('bankName', bankName)
          bankFormData.append('clabe', formData.clabe)
          if (formData.numeroCuenta) bankFormData.append('accountNumber', formData.numeroCuenta)
          bankFormData.append('currency', formData.moneda || 'MXN')
          bankFormData.append('accountHolderName', formData.nombreCuenta)
          bankFormData.append('file', bankFile)

          const bankResponse = await fetch(
            `${config.apiBaseUrl}/admin/suppliers/${supplierId}/bank-account`,
            {
              method: 'POST',
              body: bankFormData,
              headers: { 'Authorization': `Bearer ${token}` }
            }
          )
          if (!bankResponse.ok) {
            const errorData = await bankResponse.json().catch(() => ({}))
            throw new Error(errorData.message || 'Error al guardar información bancaria')
          }
        } else {
          await apiService.request(`/admin/suppliers/${supplierId}/bank-account`, {
            method: 'POST',
            body: JSON.stringify({
              bankName,
              clabe: formData.clabe,
              accountNumber: formData.numeroCuenta || undefined,
              currency: formData.moneda || 'MXN',
              accountHolderName: formData.nombreCuenta
            })
          })
        }
        markDone('bank')
      }

      // 6. Upload documents (skip if already done)
      if (!completedSteps.has('documents')) {
        if (localDocuments.length > 0) {
          setSavingProgress('Subiendo documentos...')
          const token = localStorage.getItem('access_token')
          for (const doc of localDocuments) {
            const docFormData = new FormData()
            docFormData.append('documentTypeId', doc.documentTypeId)
            docFormData.append('file', doc.file)

            await fetch(
              `${config.apiBaseUrl}/admin/suppliers/${supplierId}/documents`,
              {
                method: 'POST',
                body: docFormData,
                headers: { 'Authorization': `Bearer ${token}` }
              }
            )
          }
        }
        markDone('documents')
      }

      // 7. Send to review (skip if already done)
      if (!completedSteps.has('status')) {
        setSavingProgress('Enviando a revisión...')
        await apiService.request(`/admin/suppliers/${supplierId}/status`, {
          method: 'PATCH',
          body: JSON.stringify({
            status: 'sent_to_review',
            notes: 'Expediente completado y enviado a revisión por el administrador'
          })
        })
        markDone('status')
      }

      setSavingProgress('')
      showSuccess('¡Éxito!', 'Proveedor creado y enviado a revisión correctamente')
      setTimeout(() => {
        if (onSuccess) onSuccess()
        else onBack()
      }, 1500)
    } catch (error: unknown) {
      setSavingProgress('')
      const errorMessage = error instanceof Error ? error.message : 'Ocurrió un error al guardar el proveedor'
      showError('Error', `${errorMessage}. Puedes corregir los datos e intentar de nuevo sin perder el progreso.`)
    } finally {
      setIsSaving(false)
    }
  }

  // Navigation
  const handleNext = async () => {
    const isValid = await validateStep(currentStep)
    if (!isValid) {
      showError('Falta información', 'Por favor completa todos los campos requeridos de la pestaña actual.')
      return
    }
    if (currentStep === 5) {
      setShowConfirmModal(true)
      return
    }
    setCurrentStep((currentStep + 1) as FormStep)
  }

  const handlePrevious = () => {
    if (currentStep > 1) setCurrentStep((currentStep - 1) as FormStep)
  }

  const steps = [
    { number: 1, label: 'Datos generales' },
    { number: 2, label: 'Información fiscal' },
    { number: 3, label: 'Información bancaria' },
    { number: 4, label: 'Documentos requeridos' },
    { number: 5, label: 'Datos de acceso' }
  ]

  // Reusable field wrapper
  const fieldClass = (fieldName: keyof FormData) =>
    `w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${
      errors[fieldName]
        ? 'border-red-500 bg-red-50 focus:border-red-500'
        : watch(fieldName)
          ? 'border-green-500 bg-green-50 focus:border-green-500'
          : 'border-gray-300 bg-gray-50 focus:border-blue-500'
    }`

  const FieldIcon = ({ fieldName }: { fieldName: keyof FormData }) => {
    if (!watch(fieldName)) return null
    return (
      <div className="absolute right-3 top-1/2 -translate-y-1/2">
        {errors[fieldName] ? <XCircle className="w-5 h-5 text-red-500" /> : <CheckCircle className="w-5 h-5 text-green-500" />}
      </div>
    )
  }

  const FieldMessage = ({ fieldName }: { fieldName: keyof FormData }) => (
    <>
      {errors[fieldName] && <p className="text-xs text-red-600 mt-1">{errors[fieldName]?.message as string}</p>}
      {!errors[fieldName] && watch(fieldName) && <p className="text-xs text-green-600 mt-1">✓ Válido</p>}
    </>
  )

  return (
    <>
      <Notification
        type={notification.type}
        title={notification.title}
        message={notification.message}
        isVisible={notification.isVisible}
        onClose={hideNotification}
      />
      <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-8">
          <button onClick={handleBack} className={estiloDeBoton('secundario', 'mb-4')}>
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Completar mi expediente</h1>
          <p className="text-sm text-gray-600 mt-1">Llena el formulario con tus datos para crear tu expediente</p>
        </div>

        {/* Steps Navigation */}
        <div className="flex gap-3 mb-8">
          {steps.map((step) => (
            <button
              key={step.number}
              onClick={() => setCurrentStep(step.number as FormStep)}
              className={`flex-1 px-6 py-3 rounded-lg text-sm font-medium transition-all ${
                currentStep === step.number
                  ? 'text-white shadow-md'
                  : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300'
              }`}
              style={currentStep === step.number ? { backgroundColor: '#315FB9' } : {}}
            >
              {step.number}. {step.label}
            </button>
          ))}
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">
            {steps[currentStep - 1].label}
          </h2>

          {/* Step 1: Datos generales */}
          {currentStep === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Razón social <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input type="text" {...register('razonSocial', { required: 'La razón social es requerida', minLength: { value: 3, message: 'Mínimo 3 caracteres' } })} placeholder="------" className={fieldClass('razonSocial')} />
                  <FieldIcon fieldName="razonSocial" />
                </div>
                <FieldMessage fieldName="razonSocial" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nombre comercial</label>
                <div className="relative">
                  <input type="text" {...register('nombreComercial', { minLength: { value: 3, message: 'Mínimo 3 caracteres' } })} placeholder="Ingresa el nombre comercial" className={fieldClass('nombreComercial')} />
                  <FieldIcon fieldName="nombreComercial" />
                </div>
                <FieldMessage fieldName="nombreComercial" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">ID interno (SAI)</label>
                <div className="relative">
                  <input type="text" inputMode="numeric" maxLength={5} {...register('saiSupplierId', { validate: validarIdSai })} placeholder="42" className={fieldClass('saiSupplierId')} />
                  <FieldIcon fieldName="saiSupplierId" />
                </div>
                <FieldMessage fieldName="saiSupplierId" />
                <p className="text-xs text-gray-400 mt-1">Opcional. Número del proveedor en SAI, de 1 a 99999.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de servicio <span className="text-red-500">*</span></label>
                <div className="relative">
                  <Controller name="tipoServicio" control={control} rules={{ required: 'El tipo de servicio es requerido' }} render={({ field }) => (
                    <SelectBuscable variante="formulario" id="tipoServicio" name="tipoServicio" value={field.value ?? ''} onChange={field.onChange} error={Boolean(errors.tipoServicio)} placeholder="Seleccionar tipo de servicio" placeholderDeBusqueda="Buscar tipo de servicio…" opciones={SERVICE_TYPES.map((s) => ({ value: s.id, label: s.name }))} />
                  )} />
                </div>
                <FieldMessage fieldName="tipoServicio" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contacto principal <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input type="text" {...register('contactoPrincipal', { required: 'El contacto principal es requerido', minLength: { value: 3, message: 'Mínimo 3 caracteres' } })} placeholder="Nombre del contacto" className={fieldClass('contactoPrincipal')} />
                  <FieldIcon fieldName="contactoPrincipal" />
                </div>
                <FieldMessage fieldName="contactoPrincipal" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input type="text" {...register('telefono', { required: 'El teléfono es requerido', pattern: { value: /^\d{10}$/, message: 'Debe contener exactamente 10 dígitos' } })} placeholder="5512345678" maxLength={10} className={fieldClass('telefono')} />
                  <FieldIcon fieldName="telefono" />
                </div>
                <FieldMessage fieldName="telefono" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email de Ventas <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input type="email" {...register('correoVentas', { required: 'El email de ventas es requerido', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email no válido' } })} placeholder="ventas@ejemplo.com" className={fieldClass('correoVentas')} />
                  <FieldIcon fieldName="correoVentas" />
                </div>
                <FieldMessage fieldName="correoVentas" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email de Cuentas por Pagar</label>
                <div className="relative">
                  <input type="email" {...register('correoCuentas', { pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email no válido' } })} placeholder="cuentas@ejemplo.com" className={fieldClass('correoCuentas')} />
                  <FieldIcon fieldName="correoCuentas" />
                </div>
                <FieldMessage fieldName="correoCuentas" />
              </div>
            </div>
          )}

          {/* Step 2: Información fiscal + Domicilio */}
          {currentStep === 2 && (
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Información fiscal</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">RFC <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <input type="text" {...register('rfc', { required: 'El RFC es requerido', pattern: { value: /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/, message: 'RFC inválido' }, onChange: (e) => { e.target.value = e.target.value.toUpperCase() } })} placeholder="AAAA000000000" className={fieldClass('rfc')} />
                      <FieldIcon fieldName="rfc" />
                    </div>
                    <FieldMessage fieldName="rfc" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Régimen fiscal <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <Controller name="regimenFiscal" control={control} rules={{ required: 'El régimen fiscal es requerido' }} render={({ field }) => (
                        <SelectBuscable variante="formulario" id="regimenFiscal" name="regimenFiscal" value={field.value ?? ''} onChange={field.onChange} error={Boolean(errors.regimenFiscal)} placeholder="Seleccionar" placeholderDeBusqueda="Buscar régimen…" opciones={SAT_FISCAL_REGIMES.map((r) => ({ value: r.code, label: `${r.code} - ${r.name}` }))} />
                      )} />
                    </div>
                    <FieldMessage fieldName="regimenFiscal" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Uso de CFDI <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <Controller name="usoCFDI" control={control} rules={{ required: 'El uso de CFDI es requerido' }} render={({ field }) => (
                        <SelectBuscable variante="formulario" id="usoCFDI" name="usoCFDI" value={field.value ?? ''} onChange={field.onChange} error={Boolean(errors.usoCFDI)} placeholder="Seleccionar" placeholderDeBusqueda="Buscar uso de CFDI…" opciones={SAT_CFDI_USAGE.map((u) => ({ value: u.code, label: `${u.code} - ${u.name}` }))} />
                      )} />
                    </div>
                    <FieldMessage fieldName="usoCFDI" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Correo fiscal <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <input type="email" {...register('correoFiscal', { required: 'El correo fiscal es requerido', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Formato de correo inválido' } })} placeholder="Ej. empresa@example.com" className={fieldClass('correoFiscal')} />
                      <FieldIcon fieldName="correoFiscal" />
                    </div>
                    <FieldMessage fieldName="correoFiscal" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <input type="text" {...register('telefonoFiscal', { required: 'El teléfono es requerido', pattern: { value: /^\d{10}$/, message: 'Debe contener exactamente 10 dígitos' } })} placeholder="5512345678" maxLength={10} className={fieldClass('telefonoFiscal')} />
                      <FieldIcon fieldName="telefonoFiscal" />
                    </div>
                    <FieldMessage fieldName="telefonoFiscal" />
                    <p className="text-xs text-gray-500 mt-1">{watch('telefonoFiscal')?.length || 0}/10</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Domicilio fiscal</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">País <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <Controller name="pais" control={control} rules={{ required: 'El país es requerido' }} render={({ field }) => (
                        <SelectBuscable variante="formulario" id="pais" name="pais" value={field.value ?? ''} onChange={(valor) => { field.onChange(valor); setValue('estado', ''); setValue('municipio', ''); setValue('colonia', ''); setValue('cp', ''); setValue('numero', ''); setAvailableColonias([]) }} error={Boolean(errors.pais)} placeholder="Seleccionar" placeholderDeBusqueda="Buscar país…" opciones={[{ value: 'MX', label: 'México' }, { value: 'US', label: 'Estados Unidos' }, { value: 'OTRO', label: 'OTRO' }]} />
                      )} />
                    </div>
                    <FieldMessage fieldName="pais" />
                  </div>

                  {/* Estado/Provincia */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Estado/Provincia <span className="text-red-500">*</span></label>
                    <div className="relative">
                      {watch('pais') === 'OTRO' ? (
                        <input type="text" {...register('estado', { required: 'El estado/provincia es requerido', minLength: { value: 2, message: 'Mínimo 2 caracteres' } })} placeholder="Ingresa el estado o provincia" className={fieldClass('estado')} />
                      ) : (
                        <Controller name="estado" control={control} rules={{ required: 'El estado es requerido' }} render={({ field }) => (
                          <SelectBuscable variante="formulario" id="estado" name="estado" value={field.value ?? ''} onChange={(valor) => { field.onChange(valor); setValue('municipio', '') }} error={Boolean(errors.estado)} placeholder="Seleccionar" placeholderDeBusqueda="Buscar estado…" opciones={(watch('pais') === 'MX' ? MEXICAN_STATES : watch('pais') === 'US' ? US_STATES : []).map((e) => ({ value: e.code, label: e.name }))} />
                        )} />
                      )}
                      <FieldIcon fieldName="estado" />
                    </div>
                    <FieldMessage fieldName="estado" />
                  </div>

                  {/* CP */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CP {watch('pais') !== 'OTRO' ? <span className="text-red-500">*</span> : '(Opcional/ZIP)'}</label>
                    <div className="relative">
                      <input type="text" {...register('cp', { required: watch('pais') !== 'OTRO' ? 'El código postal es requerido' : false, pattern: watch('pais') !== 'OTRO' ? { value: /^\d{5}$/, message: 'Debe contener exactamente 5 dígitos' } : undefined })} placeholder={watch('pais') === 'OTRO' ? 'Código Postal / ZIP' : '12345'} maxLength={watch('pais') === 'OTRO' ? 10 : 5} className={fieldClass('cp')} />
                      <FieldIcon fieldName="cp" />
                    </div>
                    <FieldMessage fieldName="cp" />
                    {watch('pais') !== 'OTRO' && <p className="text-xs text-gray-500 mt-1">{watch('cp')?.length || 0}/5</p>}
                  </div>

                  {/* Municipio - solo MX/US */}
                  {watch('pais') !== 'OTRO' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Municipio <span className="text-red-500">*</span></label>
                      <div className="relative">
                        {watch('pais') === 'US' && watch('estado') && US_CITIES[watch('estado')] ? (
                          <Controller name="municipio" control={control} rules={{ required: 'El municipio es requerido' }} render={({ field }) => (
                            <SelectBuscable variante="formulario" id="municipio" name="municipio" value={field.value ?? ''} onChange={field.onChange} error={Boolean(errors.municipio)} placeholder="Seleccionar municipio/ciudad" placeholderDeBusqueda="Buscar municipio…" opciones={US_CITIES[watch('estado')].map((c) => ({ value: c, label: c }))} />
                          )} />
                        ) : (
                          <input type="text" {...register('municipio', { required: 'El municipio es requerido', minLength: { value: 3, message: 'Mínimo 3 caracteres' } })} placeholder="Ingresa el municipio" className={fieldClass('municipio')} />
                        )}
                        <FieldIcon fieldName="municipio" />
                      </div>
                      <FieldMessage fieldName="municipio" />
                    </div>
                  )}

                  {/* Colonia - solo MX/US */}
                  {watch('pais') !== 'OTRO' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Colonia <span className="text-red-500">*</span></label>
                      <div className="relative">
                        {availableColonias.length > 0 ? (
                          <Controller name="colonia" control={control} rules={{ required: 'La colonia es requerida' }} render={({ field }) => (
                            <SelectBuscable variante="formulario" id="colonia" name="colonia" value={field.value ?? ''} onChange={field.onChange} error={Boolean(errors.colonia)} placeholder="Seleccionar colonia" placeholderDeBusqueda="Buscar colonia…" opciones={availableColonias.map((c) => ({ value: c, label: c }))} />
                          )} />
                        ) : (
                          <input type="text" {...register('colonia', { required: 'La colonia es requerida', minLength: { value: 3, message: 'Mínimo 3 caracteres' } })} placeholder="Ingresa la colonia" className={fieldClass('colonia')} />
                        )}
                        <FieldIcon fieldName="colonia" />
                      </div>
                      <FieldMessage fieldName="colonia" />
                    </div>
                  )}

                  {/* Calle */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{watch('pais') === 'OTRO' ? 'Calle y número' : 'Calle'} <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <input type="text" {...register('calle', { required: 'La calle es requerida', minLength: { value: 3, message: 'Mínimo 3 caracteres' } })} placeholder={watch('pais') === 'OTRO' ? 'Ej. Avenida Principal 412' : 'Ingresa la calle'} className={fieldClass('calle')} />
                      <FieldIcon fieldName="calle" />
                    </div>
                    <FieldMessage fieldName="calle" />
                  </div>

                  {/* Número - solo MX/US */}
                  {watch('pais') !== 'OTRO' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Número <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <input type="text" {...register('numero', { required: 'El número es requerido' })} placeholder="Ingresa el número" className={fieldClass('numero')} />
                        <FieldIcon fieldName="numero" />
                      </div>
                      <FieldMessage fieldName="numero" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Información bancaria */}
          {currentStep === 3 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" style={{ paddingBottom: '6.25rem' }}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">CLABE <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input type="text" {...register('clabe', { required: 'La CLABE es requerida', pattern: { value: /^\d{18}$/, message: 'Debe contener exactamente 18 dígitos' } })} placeholder="123456789012345678" maxLength={18} className={fieldClass('clabe')} />
                  <FieldIcon fieldName="clabe" />
                </div>
                <FieldMessage fieldName="clabe" />
                <p className="text-xs text-gray-500 mt-1">{watch('clabe')?.length || 0}/18</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Número de cuenta</label>
                <div className="relative">
                  <input type="text" {...register('numeroCuenta')} placeholder="Ej. 1234567890" className={fieldClass('numeroCuenta')} />
                  <FieldIcon fieldName="numeroCuenta" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Banco <span className="text-red-500">*</span></label>
                <div className="relative">
                  <Controller name="banco" control={control} rules={{ required: 'El banco es requerido' }} render={({ field }) => (
                    <SelectBuscable variante="formulario" id="banco" name="banco" value={field.value ?? ''} onChange={field.onChange} error={Boolean(errors.banco)} placeholder="Seleccionar banco" placeholderDeBusqueda="Buscar banco…" opciones={[...MEXICAN_BANKS.map((b) => ({ value: b.name, label: b.name })), { value: 'Otro', label: 'Otro' }]} />
                  )} />
                </div>
                <FieldMessage fieldName="banco" />
              </div>

              {/* Custom Bank Name - visible when "Otro" is selected */}
              {watch('banco') === 'Otro' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nombre del banco <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input type="text" {...register('customBankName', { required: watch('banco') === 'Otro' ? 'El nombre del banco es requerido' : false, minLength: { value: 2, message: 'Mínimo 2 caracteres' } })} placeholder="Nombre del Banco" className={fieldClass('customBankName')} />
                    <FieldIcon fieldName="customBankName" />
                  </div>
                  <FieldMessage fieldName="customBankName" />
                </div>
              )}

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Nombre de cuenta <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input type="text" {...register('nombreCuenta', { required: 'El nombre de cuenta es requerido', minLength: { value: 3, message: 'Mínimo 3 caracteres' } })} placeholder="Nombre del titular de la cuenta" className={fieldClass('nombreCuenta')} />
                  <FieldIcon fieldName="nombreCuenta" />
                </div>
                <FieldMessage fieldName="nombreCuenta" />
              </div>

              {/* Bank file upload */}
              <div className="md:col-span-2">
                {bankFile ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Carátula bancaria</label>
                    <div className="inline-flex flex-col items-start">
                      <div className="relative group">
                        <a href={URL.createObjectURL(bankFile)} target="_blank" rel="noopener noreferrer" className="block">
                          {bankFile.type === 'application/pdf' ? (
                            <div className="w-20 h-24 rounded-lg border border-gray-200 bg-gray-50 flex flex-col items-center justify-center">
                              <FileText className="w-8 h-8 text-gray-400" />
                              <span className="text-[10px] text-gray-400 mt-1">PDF</span>
                            </div>
                          ) : (
                            <img src={URL.createObjectURL(bankFile)} alt="Carátula bancaria" className="w-20 h-24 object-cover rounded-lg border border-gray-200 bg-gray-50" />
                          )}
                        </a>
                        <button
                          type="button"
                          onClick={() => setBankFile(null)}
                          className="absolute -top-2 -right-2 w-5 h-5 bg-red-100 hover:bg-red-200 text-red-500 rounded-full flex items-center justify-center transition-colors"
                          title="Eliminar archivo"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      <span className="text-xs text-gray-600 mt-1.5 max-w-[120px] truncate">{bankFile.name}</span>
                    </div>
                    <label className="inline-flex items-center gap-1.5 mt-3 text-xs text-blue-600 hover:text-blue-800 cursor-pointer">
                      <Upload className="w-3.5 h-3.5" />
                      Cambiar archivo
                      <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg" onChange={(e) => { const file = e.target.files?.[0]; if (file) setBankFile(file) }} />
                    </label>
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-white">
                    <span className="text-sm font-medium text-gray-700">
                      Adjuntar carátula <span className="text-gray-500 font-normal">(PDF, PNG, JPG)</span>
                    </span>
                    <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg cursor-pointer transition-colors text-sm font-medium">
                      <Upload className="w-4 h-4" />
                      Cargar
                      <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg" onChange={(e) => { const file = e.target.files?.[0]; if (file) setBankFile(file) }} />
                    </label>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Documentos (local) */}
          {currentStep === 4 && (
            <div>
              {!detectedPersonType && (
                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    Ingresa un RFC válido en la pestaña de Información fiscal para ver los documentos correspondientes al tipo de persona ({'"'}Moral{'"'}, {'"'}Física{'"'} o {'"'}Extranjero{'"'}).
                  </p>
                </div>
              )}
              {documentTypes.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p>No hay tipos de documentos configurados</p>
                </div>
              ) : (
                <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-[#E5E7EB]">
                    <thead className="bg-[#FAF9FF]">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Documento</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Archivo</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-[#E1E1E1]">
                      {documentTypes.map((docType) => {
                        const localDoc = localDocuments.find(d => d.documentTypeId === docType.id)
                        return (
                          <tr key={docType.id} className="hover:bg-[#F4F7FB] transition-colors">
                            <td className="px-6 py-4">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{docType.name}</p>
                                {docType.description && <p className="text-xs text-gray-500">{docType.description}</p>}
                                {docType.isRequired && <span className="text-xs text-red-500 font-medium">Requerido</span>}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm">
                              {localDoc ? (
                                <div className="inline-flex items-center gap-1 text-green-600">
                                  <FileText className="w-4 h-4" />
                                  {localDoc.fileName}
                                </div>
                              ) : (
                                <label className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 underline cursor-pointer">
                                  <Upload className="w-4 h-4" />
                                  Subir archivo
                                  <input
                                    type="file"
                                    className="hidden"
                                    accept={docType.allowedMimeTypes?.join(',') || '*'}
                                    onChange={handleFileInputChange(docType.id, docType.name)}
                                  />
                                </label>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                {localDoc ? (
                                  <>
                                    <label className="p-1.5 rounded hover:bg-blue-50 text-blue-600 cursor-pointer">
                                      <PencilLine className="w-4 h-4" />
                                      <input
                                        type="file"
                                        className="hidden"
                                        accept={docType.allowedMimeTypes?.join(',') || '*'}
                                        onChange={handleFileInputChange(docType.id, docType.name)}
                                      />
                                    </label>
                                    <button onClick={() => handleLocalFileRemove(docType.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600">
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </>
                                ) : (
                                  <button disabled className="p-1.5 rounded bg-gray-100 text-gray-400 cursor-not-allowed">
                                    <PencilLine className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
                </div>
              )}
            </div>
          )}

          {/* Step 5: Datos de acceso */}
          {currentStep === 5 && (
            <div className="space-y-6">
              {/* Información del usuario */}
              <div className="bg-white border border-[#E0E0E0] rounded-lg p-6">
                <h3 className="text-lg font-semibold text-[#1A1A1A] mb-6 pb-4 border-b border-[#E0E0E0]">Información del usuario</h3>
                <div className="grid grid-cols-2 gap-4 mt-6">
                  <div>
                    <label className="block text-sm font-medium text-[#2C2C2C] mb-2">Nombre<span className="text-red-500">*</span></label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Ingresa el nombre"
                        {...register('contactFirstName', { required: 'El nombre es obligatorio', minLength: { value: 2, message: 'Mínimo 2 caracteres' }, pattern: { value: /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/, message: 'Solo se permiten letras' } })}
                        className={`w-full px-4 py-2.5 pr-10 bg-[#f5f9fb] border-0 rounded-lg focus:outline-none focus:ring-2 text-gray-900 placeholder:text-gray-400 transition-all ${errors.contactFirstName ? 'ring-2 ring-red-500 bg-red-50 focus:ring-red-500' : watch('contactFirstName') && !errors.contactFirstName ? 'ring-2 ring-green-500 bg-green-50 focus:ring-green-500' : 'focus:ring-blue-500'}`}
                        disabled={isSaving}
                      />
                      {watch('contactFirstName') && !errors.contactFirstName && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500 text-xl">✓</span>}
                      {errors.contactFirstName && watch('contactFirstName') && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500 text-xl">✗</span>}
                    </div>
                    <p className={`text-xs mt-1 font-medium transition-colors ${errors.contactFirstName ? 'text-red-500' : watch('contactFirstName') && !errors.contactFirstName ? 'text-green-600' : 'text-gray-500'}`}>
                      {errors.contactFirstName?.message || (watch('contactFirstName') && !errors.contactFirstName ? '✓ Nombre válido' : 'Mínimo 2 caracteres, solo letras')}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2C2C2C] mb-2">Apellido<span className="text-red-500">*</span></label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Ingresa el apellido"
                        {...register('contactLastName', { required: 'El apellido es obligatorio', minLength: { value: 2, message: 'Mínimo 2 caracteres' }, pattern: { value: /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/, message: 'Solo se permiten letras' } })}
                        className={`w-full px-4 py-2.5 pr-10 bg-[#f5f9fb] border-0 rounded-lg focus:outline-none focus:ring-2 text-gray-900 placeholder:text-gray-400 transition-all ${errors.contactLastName ? 'ring-2 ring-red-500 bg-red-50 focus:ring-red-500' : watch('contactLastName') && !errors.contactLastName ? 'ring-2 ring-green-500 bg-green-50 focus:ring-green-500' : 'focus:ring-blue-500'}`}
                        disabled={isSaving}
                      />
                      {watch('contactLastName') && !errors.contactLastName && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500 text-xl">✓</span>}
                      {errors.contactLastName && watch('contactLastName') && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500 text-xl">✗</span>}
                    </div>
                    <p className={`text-xs mt-1 font-medium transition-colors ${errors.contactLastName ? 'text-red-500' : watch('contactLastName') && !errors.contactLastName ? 'text-green-600' : 'text-gray-500'}`}>
                      {errors.contactLastName?.message || (watch('contactLastName') && !errors.contactLastName ? '✓ Apellido válido' : 'Mínimo 2 caracteres, solo letras')}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2C2C2C] mb-2">Dirección de email<span className="text-red-500">*</span></label>
                    <div className="relative">
                      <input
                        type="email"
                        placeholder="ejemplo@correo.com"
                        {...register('email', { required: 'El email es obligatorio', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email no válido' } })}
                        className={`w-full px-4 py-2.5 pr-10 bg-[#f5f9fb] border-0 rounded-lg focus:outline-none focus:ring-2 text-gray-900 placeholder:text-gray-400 transition-all ${errors.email ? 'ring-2 ring-red-500 bg-red-50 focus:ring-red-500' : watch('email') && !errors.email ? 'ring-2 ring-green-500 bg-green-50 focus:ring-green-500' : 'focus:ring-blue-500'}`}
                        disabled={isSaving}
                      />
                      {watch('email') && !errors.email && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500 text-xl">✓</span>}
                      {errors.email && watch('email') && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500 text-xl">✗</span>}
                    </div>
                    <p className={`text-xs mt-1 font-medium transition-colors ${errors.email ? 'text-red-500' : watch('email') && !errors.email ? 'text-green-600' : 'text-gray-500'}`}>
                      {errors.email?.message || (watch('email') && !errors.email ? '✓ Email válido' : 'Formato: ejemplo@correo.com')}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2C2C2C] mb-2">Estatus<span className="text-red-500">*</span></label>
                    <Controller name="isActive" control={control} render={({ field }) => (
                      <SelectBuscable variante="formulario" id="isActive" name="isActive" value={String(field.value)} onChange={(valor) => field.onChange(valor === 'true')} disabled={isSaving} canDelete={false} placeholderDeBusqueda="Buscar estatus…" opciones={[{ value: 'true', label: 'Activo' }, { value: 'false', label: 'Inactivo' }]} />
                    )} />
                  </div>
                </div>
              </div>

              {/* Contraseña */}
              <div className="bg-white border border-[#E0E0E0] rounded-lg p-6">
                <h3 className="text-lg font-semibold text-[#1A1A1A] mb-6 pb-4 border-b border-[#E0E0E0]">Contraseña</h3>
                <div className="space-y-6 mt-6">
                  <div className="w-1/2 pr-2">
                    <label className="block text-sm font-medium text-[#2C2C2C] mb-2">Contraseña<span className="text-red-500">*</span></label>
                    <div className="flex items-center gap-3">
                      <div className="relative flex-1">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Ingresa la contraseña"
                          {...register('password', {
                            required: 'La contraseña es obligatoria',
                            validate: (value) => {
                              const validation = validatePassword(value)
                              return Object.values(validation).every(v => v) || 'La contraseña no cumple con los requisitos'
                            }
                          })}
                          className={`w-full px-4 py-2.5 bg-[#f5f9fb] border-0 rounded-lg focus:outline-none focus:ring-2 pr-12 text-gray-900 placeholder:text-gray-400 transition-all ${errors.password ? 'ring-2 ring-red-500 bg-red-50 focus:ring-red-500' : password && isPasswordValid ? 'ring-2 ring-green-500 bg-green-50 focus:ring-green-500' : 'focus:ring-blue-500'}`}
                          disabled={isSaving}
                        />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" disabled={isSaving}>
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                      <button type="button" onClick={generatePassword} className="flex items-center gap-1.5 text-[#4A7EFF] hover:text-[#3D6FE6] text-sm font-medium whitespace-nowrap disabled:opacity-50" disabled={isSaving}>
                        <RefreshCw className="w-4 h-4" />
                        Generar
                      </button>
                    </div>
                  </div>

                  <div className="mt-8 w-1/2 pr-2">
                    <div className="text-sm font-medium text-[#2C2C2C] mb-2">Fuerza de la contraseña</div>
                    <div className="h-2 bg-[#f5f9fb] rounded-full overflow-hidden mb-3">
                      <div className={`h-full transition-all duration-300 ${isPasswordValid ? 'bg-green-500 w-full' : Object.values(passwordValidation).filter(v => v).length >= 3 ? 'bg-yellow-500 w-3/4' : Object.values(passwordValidation).filter(v => v).length >= 2 ? 'bg-orange-500 w-1/2' : (password || '').length > 0 ? 'bg-red-500 w-1/4' : 'bg-gray-300 w-0'}`}></div>
                    </div>
                    <div className="bg-[#f5f9fb] rounded-lg p-4">
                      <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm mb-3">
                        {[
                          { key: 'minLength', label: 'Al menos 8 caracteres' },
                          { key: 'hasUpperCase', label: 'Letra mayúscula' },
                          { key: 'hasLowerCase', label: 'Letra minúscula' },
                          { key: 'hasNumber', label: 'Número' },
                          { key: 'hasSpecialChar', label: 'Carácter especial' }
                        ].map(({ key, label }) => (
                          <div key={key} className={`flex items-center gap-2 transition-colors ${passwordValidation[key as keyof typeof passwordValidation] ? 'text-green-600' : (password || '').length > 0 ? 'text-red-500' : 'text-gray-500'}`}>
                            {passwordValidation[key as keyof typeof passwordValidation] ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                            <span>{label}</span>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500">Usa el generador para una contraseña segura o crea tu propia siguiendo los requisitos de arriba</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Saving progress overlay */}
        {isSaving && savingProgress && (
          <CapaDeModal>
            <div className="bg-white rounded-lg p-8 shadow-xl flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              <p className="text-gray-700 font-medium">{savingProgress}</p>
            </div>
          </CapaDeModal>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between mt-6">
          <div>{/* Spacer */}</div>

          <div className="flex items-center gap-3">
            {currentStep > 1 && (
              <button
                onClick={handlePrevious}
                disabled={isSaving}
                className={estiloDeBoton('secundario')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span>Anterior</span>
              </button>
            )}

            <button
              onClick={handleNext}
              disabled={isSaving}
              className={estiloDeBoton('primario')}
            >
              <span>{isSaving ? 'Guardando...' : (currentStep === 5 ? (createdSupplierId ? 'Reintentar envío' : 'Enviar para validación') : 'Siguiente')}</span>
              {!isSaving && (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Confirm Modal */}
        <ConfirmActionModal
          isOpen={showConfirmModal}
          title={createdSupplierId ? 'Reintentar Envío' : 'Crear Proveedor y Enviar a Revisión'}
          message={createdSupplierId
            ? `El proveedor ya fue creado. Se completarán los pasos pendientes (${7 - completedSteps.size} restantes) y se enviará a revisión.`
            : '¿Estás seguro de que deseas crear este proveedor y enviar su expediente a revisión? Se guardarán todos los datos ingresados.'}
          confirmText={createdSupplierId ? 'Reintentar' : 'Crear y Enviar'}
          variante="primario"
          onConfirm={handleFinalSubmit}
          onCancel={() => setShowConfirmModal(false)}
          isLoading={isSaving}
        />

        {/* Exit Confirmation Modal */}
        <ConfirmActionModal
          isOpen={showExitModal}
          title="¿Salir sin guardar?"
          message="Tienes información sin guardar. Para conservar tus cambios es necesario completar el paso 5 (Datos de acceso) y enviar el formulario. Si sales ahora, perderás toda la información ingresada."
          confirmText="Salir sin guardar"
          variante="peligro"
          onConfirm={() => { setShowExitModal(false); onBack() }}
          onCancel={() => setShowExitModal(false)}
          isLoading={false}
        />
      </div>
    </>
  )
}

export default CreateSupplierForm
